import { useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

export interface CopyButtonProps {
  value: string;
  label: string;
  ariaLabel?: string;
  autoFocus?: boolean;
  dataTestId?: string;
  onCopied?: (value: string) => void;
  onCopyFailed?: (message: string) => void;
}

const COPIED_RESET_MS = 1500;

export const CopyButton = ({
  value,
  label,
  ariaLabel,
  autoFocus,
  dataTestId,
  onCopied,
  onCopyFailed
}: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);
  // window.setTimeout returns a number in the browser; typed explicitly because
  // @types/node's overload would otherwise widen this to NodeJS.Timeout.
  const resetTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current !== null) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, []);

  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      onCopied?.(value);
      if (resetTimerRef.current !== null) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = window.setTimeout(() => {
        resetTimerRef.current = null;
        setCopied(false);
      }, COPIED_RESET_MS);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Copy failed';
      onCopyFailed?.(message);
    }
  };

  return (
    <Button
      type="button"
      variant="outlined"
      size="small"
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={autoFocus}
      aria-label={ariaLabel || label}
      data-testid={dataTestId || 'copy-button'}
      startIcon={copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      onClick={handleClick}>
      {copied ? 'Copied!' : label}
    </Button>
  );
};
